import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { AirAdminOrLimitedAdminRole, RolesLabelMap, manageAirAdminLabel, manageableAirAdminRoles } from '@/auth/roles';
import { useSession } from '@/auth/useSession';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { getUsers, updateUserRoles } from '@/lib/api-calls';
import { QueryKeys } from '@/lib/queries';
import { UserRoleAssignmentOptions, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

/**
 * The Air-admin items for a user action menu: a revoke item for the admin role
 * the user holds, or a promote item per role, the latter only for a full
 * `air_admin`.
 */
export const AirAdminMenuItems = ({ user }: { user: UserWithRolesAndProfiles }) => {
  const { actingUser, isImpersonating } = useSession();
  if (isImpersonating) {
    return null;
  }

  return (
    <>
      {manageableAirAdminRoles(user, actingUser).map((role) => (
        <ManageAirAdmin
          key={role}
          user={user}
          role={role}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>{manageAirAdminLabel(user, role)}</DropdownMenuItem>
          }
        />
      ))}
    </>
  );
};

export interface ManageAirAdminProps {
  trigger: ReactNode;
  user: UserWithRolesAndProfiles;
  role: AirAdminOrLimitedAdminRole;
}

export const ManageAirAdmin = ({ trigger, user, role }: ManageAirAdminProps) => {
  const [open, setOpen] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateUserRoles,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isRevoking = user.userRoles.some(({ role: userRole }) => userRole === role);
  const actionLabel = manageAirAdminLabel(user, role);
  const roleLabel = RolesLabelMap[role];

  const onSubmit = () => {
    const updatedRoles: UserRoleAssignmentOptions = isRevoking
      ? user.userRoles.filter(({ role: userRole }) => userRole !== role)
      : [...user.userRoles, { role, schoolId: null }];

    return mutateAsync({
      userId: user.id,
      roles: updatedRoles.map(({ role: userRole, schoolId }) => ({ role: userRole, schoolId })),
    })
      .then(() => {
        setOpen(false);
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
        const fullName = user.fullNameOrEmail;
        toast({
          title: `${roleLabel} ${isRevoking ? 'removed from' : 'granted to'} ${fullName}`,
        });
      })
      .catch(() => {
        toast({
          title: `${roleLabel} update failed`,
        });
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{actionLabel}</AlertDialogTitle>
          <AlertDialogDescription>
            {isRevoking
              ? `Remove ${user.fullNameOrEmail} from being an ${roleLabel}.`
              : `Add ${user.fullNameOrEmail} as an ${roleLabel}.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              {actionLabel}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
